import { useCallback, useEffect } from "react"
import { toast } from 'react-toastify'
import parsePhoneNumber from 'libphonenumber-js'
import usePrefStore from "../stores/prefs"
import useTranslation from "./useTranslation"
import { db } from "../stores/db"
import moment from 'moment'
import useTarifs from "./useTarifs"
import useRetailCollections from "./useRetailCollections"
import { computeCanSeeJewelry } from "./useCanSeeJewelry"
import { computeRetailRoleForCustomer } from "./useRetailRoleForCustomer"
import useModelsStore from "../stores/models"
import { useLiveQuery } from "dexie-react-hooks"
import { GlobalContext } from "../Contexts/GlobalContext"
import { AppWriteContext } from '../Contexts/AppWriteContext'
import type { Customer, Order, OrderLine, Address, Contact, ContactRecord, AddressRecord } from '../types'
import glob_const from '../libs/glob_const'
import { impersonationWebhookError } from '../libs/impersonation'
import { TRI_TYPE, addLinesToTri, updateTriLineAtPosition } from '../libs/triOrder'
import { canCreateRetailOrder as canCreateRetailOrderFromRules, getRetailOrderType as getRetailOrderTypeFromRules, getRetailFamilleDiscount as getRetailFamilleDiscountFromRules, isFreeForCustomer as isFreeForCustomerFromRules, getStoredDiscount as getStoredDiscountFromRules, resolveLineDiscount as resolveLineDiscountFromRules } from '../libs/retail_rules'

// ⚠️ LIMITE APPWRITE : l'attribut `status` sur la collection `orders` est un Enum
// de taille MAX 7 caractères (la size de l'enum AW ne peut pas être modifiée sans
// drop+recreate de l'attribut, donc on s'aligne sur 7). Toute nouvelle valeur
// AJOUTÉE à cet enum doit être ≤ 7 chars. Si tu changes ici, mets aussi à jour :
//   - pwa/src/types/index.ts (union OrderStatus)
//   - back/retail-batch/src/types.ts (STATUS const)
//   - back/dashboard/src/components/RecomputePermsModal.jsx (ORDER_STATUS_DISPLAY)
//   - docs/Doc Technique/20_Schema_AW.md + docs/WORKFLOW_BATCH.md
//   - AppWrite console (ajouter la nouvelle valeur à l'enum côté DEV/BETA/PROD)
// Exception : `locked4sync` (11 chars) est LOCAL-ONLY (Dexie), jamais stocké AW.
// AppWriteContext.tsx le transforme en `locked` au push (cf. ligne ~1109).
const order_status = {
   new: "new",                   // Commande en cours d'édition par le user-client (ou par un manager qui en finit l'envoi au client). Editable par les détenteurs des droits CRUD.
   new_mgr: "new_mgr",           // Retail : brouillon créé par un manager (N1/N2/admin), pas encore envoyé au user-client. Perms : manager + admins. Le client ne voit PAS. "Envoyer au client" → 'new' (webhook send-to-client, qui pose le user-client).
   wait_n2: "wait_n2",           // Retail : en attente de validation N2 (soumission → webhook submit ; tous les N2 ont les droits en même temps)
   wait_n1: "wait_n1",           // Retail : en attente de validation N1 (finale, tous les N1 en parallèle)
   error: "error",               // Retail : aucun valideur N1 attribuable (ni N1 ni N2 défini sur le user-client). Workflow bloqué, alerte admin.
   locked4sync: "locked4sync",   // Elle va bientôt être envoyée à M3. Elle n'est plus modifiable. Une fois l'envoi OK, elle passe en locked (localement, car elle est directement en locked sur AppWrite)
   locked: "locked",             // Elle va bientôt être envoyée à M3. Elle n'est plus modifiable.
   wait_m3: "wait_m3",           // Elle a été a été envoyée à M3. Elle n'est plus modifiable.
   done_m3: "done_m3",           // Elle est réapparue dans la BI avec avoir été intégrée à M3. Elle n'est plus synchro dans les commandes "locales" de l'appli et va d'ailleurs en être supprimée (pour ce faire, plus aucun utilisateur n'a de droit de lecture sur cet objet)
   deleted: "deleted",           // Marquée pour suppression. C'est temporaire le temps que la suppression soit faite sur AppWrite, sauf si pas de connexion, auquel cas ça se fera à la synchro forcée suivante.
} as const

// Statuts d'une commande « soumise/verrouillée » : elle ne nous appartient plus pour édition
// (validation N1/N2, envoi M3, intégrée…). Elle ne peut donc PAS devenir la commande courante
// (curOrder) ni être éditée/sélectionnée depuis les listes. Seules new / new_mgr sont des
// brouillons éditables.
const SUBMITTED_OR_LOCKED_STATUSES: string[] = [
   order_status.wait_n2, order_status.wait_n1, order_status.error,
   order_status.locked4sync, order_status.locked, order_status.wait_m3, order_status.done_m3,
]
const isOrderSubmittedOrLocked = (status: string | undefined | null): boolean =>
   !!status && SUBMITTED_OR_LOCKED_STATUSES.includes(status)

const type_address = {
   siege: "SIEGE",   // ===
   ecrins: "ECR",    // startWith
   pos: "BTQ",       // startWith
} as const

const tag_address = {   // Pour afficher sous icone dans les listes
   siege: { fr: "SIEGE", en: "OFFICE" },
   ecrins: { fr: "ECR", en: "ECR" },
   pos: { fr: "POS", en: "POS" },
   other: { fr: "LIVR", en: "DELIV" },
} as const

const type_order = {
   commande: 'CDE',
   implant: 'CDI',
   confie: 'CON',
   confie_facture: 'FCO',
   triangulaire: 'TRI',
   devis: 'DEV',
   retail_interne: 'ECR',
   retail_externe: 'ECF',
   retail_quota: 'KOT',
   ktm_quota: 'KTM',
} as const

const default_delivery_delay = 21      // Livraison par défaut dans 21 J

// Dédup des warnings "customer non trouvé" : chaque ID n'est loggé qu'une seule fois par session.
// Évite le spam quand une liste de commandes référence un client absent du store.
const _missingCustomerIds = new Set<string>()

const useSales = () => {
   const customers = useLiveQuery(
      async () => {
         const arr = await db.customers.toCollection().toArray() ?? []
         return arr.reduce((prev, a_cust) => {
            return { ...prev, [a_cust.id]: { ...a_cust } }
         }, {} as Record<string, Customer>)
      }
   )
   //
   const { curCustomer, setCurCustomer, curTarifRegionName, setCurTarifRegionName, setMissingTarifRegionName, curOrder, setCurOrder, localOrderId, setLocalOrderId, maxRecentItems, recentCustomers, setRecentCustomers, recentOrders, setRecentOrders, setCurTarifRegion, setCurTarifCustomer, udid } = usePrefStore();
   //
   const { setIsMissingTarifLayerVisible } = { ...GlobalContext() } as any;
   //
   const { awPushOrder, awDeleteOrder, awFixOrderPermissions, awSubmitOrder, awValidateN2, awValidateN1, awReturnToClient, awSendOrderToClient, awReconcileOrder, fetchTarifsForSingleRegion } = { ...AppWriteContext() } as any
   // const tarif_regions = useModelsStore((state) => state.tarif_regions)
   const { tarif_regions, tarifs } = useModelsStore()

   //
   const { findRegionFromCessionTarifNames, findRegionFromPublicTarifNames, isTarifLoadedForRegion, getCessPriceForModelBySKU, getMinMaxCessPriceForModel } = useTarifs()
   const { getRetailModelById } = useRetailCollections()
   const { trans } = useTranslation()

   // Gestion centralisée d'un échec de webhook de transition retail (cf. asymétrie Realtime,
   // docs/SECURITE_API_WEBHOOK.md). 401/403/404/409 = la commande a évolué / accès perdu (cache
   // stale) → toast + (si demandé) on retire la copie locale stale. Autre = erreur réseau → toast réessai.
   const onRetailWebhookError = useCallback(async (err: any, orderId: string, _cleanLocalOnStale: boolean) => {
      // Impersonation (messika-pwa#13) : refus du webhook (droit impersonator absent / cible introuvable) → message dédié,
      // sans réconciliation : la commande n'est pas périmée.
      const impErr = impersonationWebhookError(err)
      if (impErr) {
         toast.error(trans(impErr === 'forbidden' ? '?impersonation_forbidden_server' : '?impersonation_target_not_found_server'), { position: 'top-right', autoClose: 4000, theme: 'colored', toastId: 'impersonation-server-refused' })
         return
      }
      const code = err?.response?.status
      const stale = code === 401 || code === 403 || code === 404 || code === 409
      console.error('Retail webhook error', orderId, code, err?.message ?? err)
      if (stale) {
         toast.error(trans('?order_stale'), { position: 'top-right', autoClose: 4000, theme: 'colored' })
         // L'order a évolué côté serveur (statut/perms changés ailleurs, asymétrie Realtime). On
         // RESYNCHRONISE la copie locale avec la vérité serveur : elle prend le bon statut (→ bascule
         // vers « Soumises ») ou est purgée si on n'y a plus accès. Évite de rester bloqué sur un
         // 'new' périmé qui re-déclenche l'erreur à chaque tentative. (cleanLocalOnStale obsolète :
         // la réconciliation décide elle-même update vs delete.)
         try { await awReconcileOrder(orderId) } catch { /* erreur infra : on laisse la copie locale telle quelle */ }
      } else {
         toast.error(trans('?order_action_failed'), { position: 'top-right', autoClose: 3000, theme: 'colored' })
      }
   }, [trans, awReconcileOrder])

   const sortOnIdString = useCallback((a: { id: string }, b: { id: string }) => {
      return a.id.localeCompare(b.id)
   }, [])

   /*
    ██████ ██    ██ ███████ ████████  ██████  ███    ███ ███████ ██████  ███████
   ██      ██    ██ ██         ██    ██    ██ ████  ████ ██      ██   ██ ██
   ██      ██    ██ ███████    ██    ██    ██ ██ ████ ██ █████   ██████  ███████
   ██      ██    ██      ██    ██    ██    ██ ██  ██  ██ ██      ██   ██      ██
    ██████  ██████  ███████    ██     ██████  ██      ██ ███████ ██   ██ ███████
   */

   /**
    * Retourne simplement l'objet customers (JSON) complet, avec tous les customers
    */
   const getCustomers = useCallback(() => customers, [customers])

   /**
    * Retourne un tableau de customers :
    */
   const getCustomersAsArray = useCallback((): Customer[] => {
      const obj = getCustomers()

      if (!obj) {
         return []
      }
      return Object.keys(obj).map((id) => {
         return obj[id]
      })
   }, [getCustomers])

   /**
    * Retourne un tableau de customers, trié sur le champ dont le nom est passé en 1er paramètre.
    * Possibilités : [name, shortname, id]
    */
   const getCustomersAsSortedArray = useCallback((field = "name", fallback_field = "shortname"): Customer[] => {
      return getCustomersAsArray().sort((a, b) => {
         let val_a, val_b

         val_a = (a as any)[field] || a.id || (a as any)[fallback_field]
         val_b = (b as any)[field] || b.id || (b as any)[fallback_field]
         return val_a.localeCompare(val_b)
      })
   }, [getCustomersAsArray])

   /**
    * Retourne un seul objet customer
    * ou null si pas trouvé
    */
   const getCustomerById = useCallback((id: string | null, tag?: unknown): Customer | null => {
      if (!id) {
         return null
      }

      const all_customers = getCustomers()
      if (!all_customers) {
         return null
      }
      const a_customer = all_customers[id]

      if (a_customer) {
         return a_customer
      }
      // Faux clients locaux (KTM, prospects, MKT, …) : non présents dans `getCustomers()`,
      // gérés via Dexie/`addFakeLocalCustomer`. On retourne null silencieusement.
      // Heuristique : un ID qui contient un underscore (KTM_RET, MKT_RET, P_xxxx, etc.) est un faux client local.
      if (id.startsWith(glob_const.prefix_ktm) || id.startsWith(glob_const.prefix_prospect) || id.includes('_')) {
         return null
      }
      // Vrai client référencé mais absent du store (sync incomplète, customer filtré, etc.) :
      // log une seule fois par ID pour ne pas spammer la console.
      if (!_missingCustomerIds.has(id)) {
         _missingCustomerIds.add(id)
         console.warn(`getCustomerById(${id}) - Pas trouvé dans le store local`)
      }
      return null
   }, [getCustomers])

   /**
    * Retourne l'adresse du SIEGE d'un objet customer
    * ou null si pas trouvée
    */
   const getSiegeFromCustomer = useCallback((customer: Customer | null | undefined): Address | undefined => {
      return customer?.addresses?.SIEGE
   }, [])

   /**
    * Retourne l'adresse du SIEGE d'un customer
    * ou null si pas trouvée
    */
   const getSiegeForCustomerId = useCallback((id: string | null): Address | undefined => {
      const a_customer = getCustomerById(id)

      return getSiegeFromCustomer(a_customer)
   }, [getCustomerById, getSiegeFromCustomer])

   /**
    * Retourne les contacts d'un objet customer sous forme d'un objet.
    * ou null si pas trouvée
    */
   const getContactsFromCustomer = useCallback((customer: Customer | null | undefined): ContactRecord | undefined => {
      return customer?.contacts
   }, [])

   /**
    * Retourne les contacts d'un objet customer sous forme d'un tableau.
    * ou null si pas trouvée
    */
   const getContactsFromCustomerAsArray = useCallback((customer: Customer | null | undefined): (Contact & { id: string })[] | null => {
      const contacts = getContactsFromCustomer(customer)

      if (contacts) {
         return Object.keys(contacts).map((id) => {
            return { ...contacts[id], id }
         })
      } else {
         return null
      }
   }, [getContactsFromCustomer])

   /**
    * Idem mais en triant sur l'ID
    */
   const getContactsFromCustomerAsSortedArray = useCallback((customer: Customer | null | undefined): (Contact & { id: string })[] | null => {
      const contacts = getContactsFromCustomerAsArray(customer)
      if (contacts) {
         return contacts.sort(sortOnIdString)
      } else {
         return null
      }
   }, [getContactsFromCustomerAsArray, sortOnIdString])

   /**
    * Retourne les adresses d'un objet customer sous forme d'un objet.
    * ou null si pas trouvée
    */
   const getAddressesFromCustomer = useCallback((customer: Customer | null | undefined): AddressRecord | undefined => {
      return customer?.addresses
   }, [])

   /**
    * Retourne les adresses d'un objet customer sous forme d'un tableau.
    * ou null si pas trouvée
    */
   const getAddressesFromCustomerAsArray = useCallback((customer: Customer | null | undefined): (Address & { id: string })[] | null => {
      const addresses = getAddressesFromCustomer(customer)

      if (addresses) {
         return Object.keys(addresses).map((id) => {
            return { ...addresses[id], id }
         })
      } else {
         return null
      }
   }, [getAddressesFromCustomer])

   /**
    * Trie un tableau d'adresses sur le type (SIEGE > BIJOUX > ECRINS)
    */
   const sortCustomerAddresses = (addresses: (Address & { id: string })[] | null | undefined): (Address & { id: string })[] | null => {
      if (addresses) {
         return addresses.sort((a, b) => {
            if (a.id === type_address.siege) {
               if (b.id === type_address.siege) { // Cas impossible en principe il n'y a qu'un seul SIEGE !!!
                  return a.id.localeCompare(b.id)
               } else {
                  return -1   // a vient avant b
               }
            } else if (b.id === type_address.siege) {
               return 1   // b vient avant a
            } else if (a.id.startsWith(type_address.ecrins)) {
               if (b.id.startsWith(type_address.ecrins)) {
                  return a.id.localeCompare(b.id)
               } else {
                  return 1    // b vient avant a
               }
            } else if (b.id.startsWith(type_address.ecrins)) {
               return -1    // a vient avant b
            } else { // Les 2 sont de type bijoux
               return a.id.localeCompare(b.id)
            }
         })
      } else {
         return null
      }
   }

   /**
    * Idem mais en triant sur le type (SIEGE > BIJOUX > ECRINS)
    */
   const getAddressesFromCustomerAsSortedArray = useCallback((customer: Customer | null | undefined): (Address & { id: string })[] | null => {
      const addresses = getAddressesFromCustomerAsArray(customer)
      return sortCustomerAddresses(addresses)
   }, [getAddressesFromCustomerAsArray])

   /**
    * Retourne le 1er numéro de téléphone d'un customer
    * ou null si pas trouvé
    */
   const getPhoneForCustomerId = useCallback((id: string | null, format = false): string | null => {
      const a_customer = getCustomerById(id)
      const siege = getSiegeForCustomerId(id)

      if (a_customer?.phones) {
         let phone = a_customer.phones[0]
         if (format && phone) {
            let num = parsePhoneNumber(phone.replaceAll(' ', ''), (siege?.country ?? 'FR') as any)
            if (num) {
               phone = num.formatInternational()
            }
         }
         return phone
      } else {
         return null
      }
   }, [getCustomerById, getSiegeForCustomerId])

   const updateRecentCustomers = useCallback((customer_id: string) => {
      setRecentCustomers([...recentCustomers.reduce((prev: string[], cur: string) => {
         const exists = getCustomerById(cur)

         if (!exists) {
            return [...prev]
         } else {
            if (cur === customer_id) {
               return [...prev]
            } else {
               return [...prev, cur]
            }
         }
      }, [customer_id]).slice(0, maxRecentItems)])
   }, [getCustomerById, maxRecentItems, recentCustomers, setRecentCustomers])

   const setCustomerIdAsCurrent = useCallback((id: string, raz_cur_order = true) => {
      const a_customer = getCustomerById(id)

      if (!a_customer) {
         console.error(`setCustomerIdAsCurrent(${id}) - Pas trouvé !`);
      } else {
         // Ignorer la vérification tarif pour les clients fictifs (KTM, prospect)
         const isFakeCustomer = id.startsWith(glob_const.prefix_ktm) || id.startsWith(glob_const.prefix_prospect)

         // "tariff": { "other": { "ppu": "CES999", "ces": "PPU999" } },
         let region_name = findRegionFromCessionTarifNames(a_customer.tarifs.ces.tarif, a_customer.tarifs.ces.currency)
         if (!region_name) {
            // !!!!! NB : OUI, c'est bien CES qui est pris dans a_customer pour chercher la région. C'est pour les clients qui n'ont pas de tarif cession mais un tarif public remisé
            region_name = findRegionFromPublicTarifNames(a_customer?.tarifs.ces.tarif, a_customer?.tarifs.ces.currency)
         }

         console.log('===', a_customer.tarifs.ces.tarif, a_customer.tarifs.ces.currency, region_name);
         setCurCustomer(id)
         if (!isFakeCustomer) {
            console.log('isTarifLoadedForRegion', region_name, isTarifLoadedForRegion(region_name));
            if (isTarifLoadedForRegion(region_name)) {
               setCurTarifRegionName(region_name)
            } else {
               setIsMissingTarifLayerVisible(true)
               setMissingTarifRegionName(region_name)
            }
         }

         if (raz_cur_order) {
            setCurOrder(null)
         }
         updateRecentCustomers(id)
      }
   }, [findRegionFromCessionTarifNames, findRegionFromPublicTarifNames, getCustomerById, isTarifLoadedForRegion, setCurCustomer, setCurOrder, setCurTarifRegionName, setIsMissingTarifLayerVisible, setMissingTarifRegionName, updateRecentCustomers])

   // Sélectionne un customer comme COURANT (curCustomer + région tarif) et télécharge son tarif
   // SILENCIEUSEMENT si absent (pas de popup MissingTarifLayer). Sert à rattacher le contexte
   // client+tarif sans interaction — ex. entrée en mode validation d'une commande retail : le
   // valideur (N1/N2/admin) doit voir les PU du client de la commande, tarif chargé au besoin.
   // Ne touche PAS à curOrder (une commande soumise/en validation n'est pas la commande courante).
   const selectCustomerWithTarif = useCallback(async (id: string) => {
      const a_customer = getCustomerById(id)
      if (!a_customer) {
         console.warn(`selectCustomerWithTarif : customer ${id} introuvable`)
         return
      }
      if (curCustomer !== id) {
         setCurCustomer(id)
         updateRecentCustomers(id)
      }
      // Clients fictifs (KTM / prospect) : pas de tarif à charger.
      if (id.startsWith(glob_const.prefix_ktm) || id.startsWith(glob_const.prefix_prospect)) return
      const region_name = findRegionFromCessionTarifNames(a_customer.tarifs.ces.tarif, a_customer.tarifs.ces.currency)
         ?? findRegionFromPublicTarifNames(a_customer.tarifs.ces.tarif, a_customer.tarifs.ces.currency)
      if (!region_name) {
         console.warn(`selectCustomerWithTarif : pas de région pour ${id} (tarif ${a_customer.tarifs.ces.tarif}/${a_customer.tarifs.ces.currency}) — customer sélectionné, tarif à charger manuellement`)
         return
      }
      if (isTarifLoadedForRegion(region_name)) {
         setCurTarifRegionName(region_name)
      } else {
         fetchTarifsForSingleRegion(
            region_name,
            false,
            () => setCurTarifRegionName(region_name),
            (err: string) => console.warn('selectCustomerWithTarif : DL tarif KO', err),
         )
      }
   }, [getCustomerById, curCustomer, setCurCustomer, updateRecentCustomers, findRegionFromCessionTarifNames, findRegionFromPublicTarifNames, isTarifLoadedForRegion, setCurTarifRegionName, fetchTarifsForSingleRegion])

   // Si la base locale ne contient qu'un seul vrai customer (hors KTM / prospect),
   // on l'auto-sélectionne et on télécharge silencieusement son tarif si absent.
   // No-op s'il y en a 0 ou plusieurs.
   const autoSelectSingleCustomer = useCallback(async () => {
      const all = await db.customers.toCollection().toArray()
      const realCustomers = all.filter(c => !c.id.startsWith(glob_const.prefix_ktm) && !c.id.startsWith(glob_const.prefix_prospect))
      if (realCustomers.length !== 1) return
      await selectCustomerWithTarif(realCustomers[0].id)
   }, [selectCustomerWithTarif])

   useEffect(() => {
      const a_customer = getCustomerById(curCustomer)

      let valCES: string | null = null
      let valPPU: string | null = null
      let valCurrencyCES: string | null = null
      let valCurrencyPPU: string | null = null
      let valHJO_CES: string | null = null
      let valHJO_PPU: string | null = null
      let valCurrencyHJO_CES: string | null = null
      let valCurrencyHJO_PPU: string | null = null

      if (curTarifRegionName) {
         const full_tarif = tarif_regions[curTarifRegionName]

         if (full_tarif) {
            //       { "hjo": { "ces": { "currency": "EUR", "tarif": "CES017" }, "ppu": { "currency": "EUR", "tarif": "PPU017" } }, "other": { "ces": { "currency": "EUR", "tarif": "CES017" }, "ppu": { "currency": "EUR", "tarif": "PPU017" } } },

            valCES = full_tarif.currencies.other.ces.tarif
            valPPU = full_tarif.currencies.other.ppu.tarif
            valCurrencyCES = full_tarif.currencies.other.ces.currency
            valCurrencyPPU = full_tarif.currencies.other.ppu.currency
            valHJO_CES = full_tarif.currencies.hjo.ces.tarif
            valHJO_PPU = full_tarif.currencies.hjo.ppu.tarif
            valCurrencyHJO_CES = full_tarif.currencies.hjo.ces.currency
            valCurrencyHJO_PPU = full_tarif.currencies.hjo.ppu.currency
         }
      }
      setCurTarifRegion({
         hjo: {
            ces: {
               tarif: valHJO_CES,
               currency: valCurrencyHJO_CES,
            },
            ppu: {
               tarif: valHJO_PPU,
               currency: valCurrencyHJO_PPU,
            },
         },
         other: {
            ces: {
               tarif: valCES,
               currency: valCurrencyCES,
            },
            ppu: {
               tarif: valPPU,
               currency: valCurrencyPPU,
            },
         },
      })
      if (!a_customer) {
         setCurTarifCustomer(null)
      } else {
         let region_name = findRegionFromCessionTarifNames(a_customer.tarifs.ces.tarif, a_customer?.tarifs.ces.currency)
         if (!region_name) {
            // !!!!! NB : OUI, c'est bien CES qui est pris dans a_customer pour chercher la région. C'est pour les clients qui n'ont pas de tarif cession mais un tarif public remisé
            region_name = findRegionFromPublicTarifNames(a_customer?.tarifs.ces.tarif, a_customer?.tarifs.ces.currency)
         }

         const a_tarif = (tarif_regions ? tarif_regions[region_name] : null)

         if (a_tarif) {
            setCurTarifCustomer({
               hjo: {
                  ces: {
                     tarif: valHJO_CES,
                     currency: valCurrencyHJO_CES,
                  },
                  ppu: {
                     tarif: valHJO_PPU,
                     currency: valCurrencyHJO_PPU,
                  },
               },
               other: {
                  ces: {
                     tarif: a_tarif.currencies.other.ces.tarif,
                     currency: a_tarif.currencies.other.ces.currency,
                  },
                  ppu: {
                     tarif: a_tarif.currencies.other.ppu.tarif,
                     currency: a_tarif.currencies.other.ppu.currency,
                  },
               },
            })
         } else {
            setCurTarifCustomer(null)
         }
      }
   }, [curCustomer, curTarifRegionName, findRegionFromCessionTarifNames, findRegionFromPublicTarifNames, getCustomerById, setCurTarifCustomer, setCurTarifRegion, setCurTarifRegionName, tarif_regions, tarifs])

   /*
    ██████  ██████  ██████  ███████ ██████  ███████
   ██    ██ ██   ██ ██   ██ ██      ██   ██ ██
   ██    ██ ██████  ██   ██ █████   ██████  ███████
   ██    ██ ██   ██ ██   ██ ██      ██   ██      ██
    ██████  ██   ██ ██████  ███████ ██   ██ ███████
   */

   /**OK
    * Retourne une collection (Dexie) d'objet orders (JSON), avec tous les orders.
    * Si sync === true : uniquement les commandes synchronisées (provenant de la BI)
    * Si sync === false : uniquement les non synchro (locales, ou envoyées vers M3 mais pas encore revenues dans la BI)
    */
   const _getOrders = useCallback((sync: boolean) => {
      if (sync) {
         return db.bi_orders.toCollection()
      } else {
         return db.orders.toCollection()
      }
   }, [])

   const getSyncOrders = useCallback(() => {
      return _getOrders(true)
   }, [_getOrders])

   const getUnsyncOrders = useCallback(() => {
      return _getOrders(false)
   }, [_getOrders])

   /**OK
    * Retourne PROMISE, then :
    * ...un tableau d'objets order
    */
   const _getOrdersAsArray = useCallback((sync: boolean): Promise<Order[]> => {
      return _getOrders(sync).toArray()
   }, [_getOrders])

   const getSyncOrdersAsArray = useCallback((): Promise<Order[]> => {
      return _getOrdersAsArray(true)
   }, [_getOrdersAsArray])

   const getUnsyncOrdersAsArray = useCallback((): Promise<Order[]> => {
      return _getOrdersAsArray(false)
   }, [_getOrdersAsArray])

   /** PAS DE SORTED ARRAY */

   /**OK
    * Retourne PROMISE, then :
    * ...un seul objet order
    * ou null si pas trouvé
    */
   const getOrderById = useCallback(async (id: string | null): Promise<Order | null> => {
      if (!id) {
         return null
      }

      let ret = await db.orders.where('id').equals(id).first()
      if (!ret) {
         ret = await db.bi_orders.where('id').equals(id).first()
      }

      return ret || null
   }, [])

   /**OK
    * Retourne PROMISE, then :
    * ...un tableau d'objets order
    * ou [] si pas trouvé
    */
   const getOrdersByIds = useCallback(async (ids: string[] | null | undefined): Promise<Order[]> => {
      if (!(ids?.length)) {
         return []
      }

      const ret = await db.orders.where('id').anyOf([...ids]).toArray()
      const ret_bi = await db.bi_orders.where('id').anyOf([...ids]).toArray()
      const all = [...ret, ...ret_bi]

      return all
   }, [])

   const removeOrderIdFromRecentOrders = useCallback(async (order_id: string) => {
      console.log('removeOrderIdFromRecentOrders', order_id);
      const new_list = recentOrders.filter((an_id) => an_id !== order_id)
      console.log('removeOrderIdFromRecentOrders', new_list);
      setRecentOrders(new_list)
   }, [recentOrders, setRecentOrders])

   const updateRecentOrders = useCallback(async (order_id: string) => {
      const new_id = String(order_id)
      const res: string[] = []
      let exists = await getOrderById(new_id)

      if (exists) {
         res.push(new_id)
      }
      for (const cur of recentOrders) {   // Plus simple qu'un `reduce()` quand il y a des appels async
         exists = await getOrderById(cur)

         if (exists && (cur !== new_id)) {
            res.push(cur)
         }
      }

      setRecentOrders(res.slice(0, maxRecentItems))
   }, [getOrderById, maxRecentItems, recentOrders, setRecentOrders])

   const setOrderIdAsCurrent = useCallback(async (id: string) => {
      const an_order = await getOrderById(id)

      // Une commande soumise/verrouillée ne peut PAS devenir la commande courante : elle ne nous
      // appartient plus pour édition. La validation N1/N2 se fait via la table de validation
      // (isValidationMode), pas via curOrder. On se base sur le STATUT (source fiable) et non sur le
      // flag local `readonly`, qui peut être faux (ex. une wait_n1 « vue » arrivée readonly=false).
      if (!isOrderSubmittedOrLocked(an_order?.status)) {
         setCurOrder(id)
         setCustomerIdAsCurrent(an_order.customer_id, false)   // false = on ne ràz pas curOrder, bien sûr !!!
         updateRecentOrders(id)
      } else {
         console.warn(`setOrderIdAsCurrent(${id}) refusé : commande soumise/verrouillée (statut '${an_order?.status}')`)
      }
   }, [getOrderById, setCurOrder, setCustomerIdAsCurrent, updateRecentOrders])


   /**OK
    * Retourne un tableau d'IDs d'objets order
    */
   const getOrderIdsForCustomerId = useCallback(async (id: string): Promise<string[]> => {
      const arr = await db.orders.where('customer_id').equals(id).toArray()
      const arr_bi = await db.bi_orders.where('customer_id').equals(id).toArray()
      return [...arr, ...arr_bi].map(an_order => an_order.id as string)
   }, [])

   /**OK
    * Retourne une collection d'objets order
    * Si sync === true : uniquement les commandes synchronisées (provenant de la BI)
    * Si sync === false : uniquement les non synchro (locales, ou envoyées vers M3 mais pas encore revenues dans la BI)
    */
   const _getOrdersForCustomerId = useCallback(async (id: string | null, sync: boolean): Promise<Order[] | null> => {
      console.log('_getOrdersForCustomerId', id, sync);
      if (!id) {
         return null
      } else {
         if (sync) {
            return await db.bi_orders.where('customer_id').equals(id).toArray()
         } else {
            // return await db.orders.where('customer_id', 'status').equals(id, sync).toArray()
            return await db.orders.where('customer_id').equals(id).toArray()
         }
      }
   }, [])

   const getSyncOrdersForCustomerId = useCallback(async (id: string): Promise<Order[] | null> => {
      return await _getOrdersForCustomerId(id, true)
   }, [_getOrdersForCustomerId])

   const getUnsyncOrdersForCustomerId = useCallback(async (id: string): Promise<Order[] | null> => {
      return await _getOrdersForCustomerId(id, false)
   }, [_getOrdersForCustomerId])

   /**
    * Calcule le prix total des lignes d'un objet commande.
    * Pour ECR/ECF/KOT/KTM, si `line.price` est absent, résout le prix via
    * le tarif cession de la région du client (aligné sur OrderDetail).
    * Pour les KTM (faux client KTM_xxx sans tarif assigné), fallback sur
    * la région courante sélectionnée dans le menu (curTarifRegionName).
    */
   const calcTotalForOrder = useCallback((an_order: Order | null | undefined): number => {
      if (!an_order?.lines?.length) return 0

      const isECRorECF =
         an_order.type === type_order.retail_interne ||
         an_order.type === type_order.retail_externe
      const isRetailLike = isECRorECF ||
         an_order.type === type_order.retail_quota ||
         an_order.type === type_order.ktm_quota

      const customer = isRetailLike ? getCustomerById(an_order.customer_id) : null
      let region: string | null | undefined = null
      if (isRetailLike) {
         if (customer) {
            region = findRegionFromCessionTarifNames(customer.tarifs.ces.tarif, customer.tarifs.ces.currency)
               ?? findRegionFromPublicTarifNames(customer.tarifs.ces.tarif, customer.tarifs.ces.currency)
         }
         if (!region) region = curTarifRegionName
      }

      return an_order.lines.reduce((prev, a_line) => {
         // ECR/ECF : si la grille dit "gratuit" (100 ou NF) pour la famille de cette ligne,
         // on compte 0 quel que soit le discount stocké (NF stocke discount=0).
         if (isECRorECF && customer) {
            const m = getRetailModelById(a_line.sku)
            if (isFreeForCustomerFromRules(m?.fam, customer)) return prev
         }
         const resolved = a_line.price ?? (region ? getMinMaxCessPriceForModel(a_line.sku, region)?.min ?? 0 : 0)
         const discountFactor = 1 - (Math.max(0, Math.min(100, a_line.discount ?? 0)) / 100)
         return prev + resolved * a_line.qty * discountFactor
      }, 0)
   }, [curTarifRegionName, getCustomerById, findRegionFromCessionTarifNames, findRegionFromPublicTarifNames, getMinMaxCessPriceForModel, getRetailModelById])

   /**
    * Retourne le nb d'items d'un objet commande
    */
   const getItemsCountForOrder = useCallback((an_order: Order | null | undefined): number => {
      return an_order?.lines?.reduce((prev, a_line) => {
         return prev + a_line.qty
      }, 0) ?? 0
   }, [])

   /**
    * Création d'un objet modèle de commande locale
    */
   const makeTemplateOrder = useCallback((customer_id: string | null = null): Order => {
      const now = moment()
      return {
         /**
          * ATTENTION, ce modèle doit être compatible avec AppWriteContext._buildOrderObjectFromAppWriteObject()
          */
         id: null,   // Sera remplacé au moment du dexie_db.add()
         customer_id: customer_id ?? curCustomer,
         type: type_order.commande,
         date: now.format("YYYY-MM-DD"),
         time: now.format("HH:mm"),
         delivery: now.add(default_delivery_delay, 'days').format("YYYY-MM-DD"),
         addresses: {
            jewels: null,
            ecrins: null,
         },
         comment: null,
         mark: null,
         signature: null,
         lines: [] as OrderLine[],
         // synced: false,
         readonly: false,
         status: order_status.new,
         last_pushed: null,
         last_updated: null,
      }
   }, [curCustomer])

   /**
    * UTILITAIRE UNIQUE : blinde le `discount` des lignes RETAIL (ECR/ECF) depuis la grille.
    * À appeler par TOUT chemin qui écrit `order.lines` (création, ajout/maj, suppression,
    * clone, validation) pour qu'aucune ligne retail ne parte sans discount (le push M3 bloque sinon).
    *   - commande non-retail            → lignes inchangées
    *   - customer inconnu               → inchangé (on n'écrase pas par un 0 erroné)
    *   - ligne commentaire / sku hors grille (getStoredDiscount=null) → inchangée
    * Mapping = getStoredDiscount (NF→0, vraie remise→100, facturé→0).
    */
   const applyRetailDiscounts = useCallback((an_order: Order | null | undefined, lines: OrderLine[]): OrderLine[] => {
      if (!an_order) return lines
      const isRetail = an_order.type === type_order.retail_interne || an_order.type === type_order.retail_externe
      if (!isRetail) return lines
      const customer = getCustomerById(an_order.customer_id)
      if (!customer) return lines
      return (lines ?? []).map(l => {
         if (l.comment) return l   // ligne commentaire : pas un article, pas de discount
         // getStoredDiscount : 0 (facturé) / 100 (vraie remise) / null (sku hors grille,
         // famille interdite pour ce client, ou modèle introuvable). Un vrai 100% renvoie 100,
         // jamais null → on peut sans risque défaulter null à 0 (plein tarif). Ainsi AUCUNE
         // ligne retail ne reste sans discount (le push M3 ne bloque plus).
         const stored = getStoredDiscountFromRules(getRetailModelById(l.sku)?.fam, customer)
         return { ...l, discount: resolveLineDiscountFromRules(an_order.type, stored, l.discount) }
      })
   }, [getCustomerById, getRetailModelById])

   /**
    * Création d'une commande locale
    */
   const createOrder = useCallback(async (an_order: Order, opts: { quiet?: boolean, forceStatus?: Order['status'] } = {}): Promise<string> => {
      const short_udid = udid.substring(0, 4)
      let loop = 100
      let orderId = Number(localOrderId)
      let orderIdStr = ''
      // Statut initial : si le créateur est un manager (= pas un user-client) ET que la
      // commande est retail (ECR/ECF), on démarre en 'new_mgr' (= brouillon manager, le
      // client ne voit pas tant qu'on n'a pas cliqué "Envoyer au client"). Sinon 'new'.
      // forceStatus court-circuite cette logique (ex. reliquat = directement visible au client).
      const isRetailType = an_order.type === type_order.retail_interne || an_order.type === type_order.retail_externe
      const currentRetailRole = usePrefStore.getState().retailRole
      const isManagerCreator = currentRetailRole !== 'client'
      const initialStatus = opts.forceStatus ?? ((isRetailType && isManagerCreator) ? order_status.new_mgr : order_status.new)
      // Blindage discount (retail) — couvre clone, reliquat, et toute création avec lignes.
      const linesToStore = applyRetailDiscounts(an_order, an_order.lines ?? [])
      // Génération d'ID robuste : on cherche un `<short_udid>-<n>` libre EN LOCAL **et SUR AW**.
      // La seule vérif locale ne suffit pas : un ID peut exister sur AW sans être dans la base
      // locale (compteur localOrderId régressé après un reset/login, doc d'un autre user, etc.).
      // Dans ce cas createDocument renvoie 409 (et l'updateDocument de repli renverrait 401 si le
      // doc n'est pas à nous) → on régénère un ID suivant. Cf. bug reliquat non créé (collision a8a6-2).
      let pushed = false
      while (loop > 0 && !pushed) {
         orderIdStr = String(`${short_udid}-${orderId}`)

         console.log('createOrder (trying)', orderId, { ...an_order, id: orderIdStr, status: initialStatus });
         try {
            await db.orders.add({ ...an_order, id: orderIdStr, status: initialStatus, lines: linesToStore });
         } catch (err: any) {
            if (err.name === 'ConstraintError') {
               console.log(`orderId ${orderId} already exists (local), trying next one (remaining: ${loop})`);
               orderId = orderId + 1
               loop = loop - 1
               continue
            }
            throw err
         }
         // Local OK → push AW en mode "échec si conflit" : un 409 = ID déjà pris sur AW → on
         // annule la copie locale et on régénère. fix-permissions ensuite (le user-client doit
         // pouvoir RWD sa commande ; sans ça, perms par défaut admin-only → 401 à la 1re modif).
         try {
            await awPushOrder(orderIdStr, { failOnConflict: true })
            pushed = true
         } catch (e: any) {
            if (e?.awIdConflict) {
               console.log(`orderId ${orderId} already exists (AW), trying next one (remaining: ${loop})`);
               try { await db.orders.delete(orderIdStr) } catch { /* ignore */ }
               orderId = orderId + 1
               loop = loop - 1
            } else {
               // Autre erreur de push (réseau, perms collection…) : la copie locale est conservée,
               // elle sera repoussée plus tard. On sort (l'ID local est valide).
               pushed = true
            }
         }
      }
      // fix-permissions = mécanisme RETAIL (pose user-clients + admins en REMPLAÇANT tout le set).
      // Ne PAS l'appliquer aux commandes bijoux/quota : il écraserait leurs droits par défaut
      // (créateur) → régression "plus aucun droit". Le webhook /orders/:id/fix-permissions est retail-only.
      if (isRetailType) {
         awFixOrderPermissions(orderIdStr)
      }

      // quiet = mode création en masse (ex. déploiement KTM) : ne pas toucher à curOrder / curCustomer
      // ni aux commandes récentes, pour éviter les effets de bord (ex. téléchargement de tarif sur switch de client).
      if (!opts.quiet) {
         setCurOrder(orderIdStr)
         setCustomerIdAsCurrent(an_order.customer_id, false)   // false = on ne ràz pas curOrder, bien sûr !!!
         updateRecentOrders(orderIdStr)
      }

      const curId = Number(orderId)
      setLocalOrderId(String(curId + 1))
      return orderIdStr
   }, [localOrderId, awPushOrder, awFixOrderPermissions, setCurOrder, setCustomerIdAsCurrent, setLocalOrderId, udid, updateRecentOrders, applyRetailDiscounts])

   /**
    * Création ou MàJ de lignes dans une commande
    * La MàJ se fait sur la base du SKU
    * La MàJ ne change que les attributs présents dans les objets du tableau new_lines, donc pas de màj du
    * commentaire si l'attribut comment n'est pas présent.
    * Le seul attribut obligatoire est le SKU
    * update_line_num : si pas null indique le numéro de la ligne mise à jour. Pas de nettoyage !
    */
   const createOrUpdateLinesInOrder = useCallback(async (order_id: string, new_lines: OrderLine[], update_line_num: number | null = null) => {
      const an_order = await getOrderById(order_id)

      let cur_lines = an_order.lines

      if (an_order.type === TRI_TYPE) {
         // Triangulaire : lignes de qté 1 uniquement, jamais de fusion, ligne visée par sa position
         if (update_line_num && new_lines.length === 1 && update_line_num <= cur_lines.length) {
            cur_lines = updateTriLineAtPosition(cur_lines, update_line_num, new_lines[0])
         } else {
            cur_lines = addLinesToTri(cur_lines, new_lines)
         }
         await db.orders.put({ ...an_order, lines: [...cur_lines] });
         awPushOrder(order_id)
         return
      }

      if (!update_line_num ||    // On n'est pas en update d'une ligne
         (new_lines.length > 1) ||  // On a fourni plus d'une ligne
         (cur_lines.length < update_line_num) ||   // Le numéro de ligne a updater est trop grand
         (cur_lines[update_line_num - 1].sku !== new_lines[0].sku)   // Le SKU a updater ne matche pas !
      ) {
         // Alors on ajoute la ou les lignes sans chercher à updater...
         for (const a_line of new_lines) {
            // On cherche une ancienne ligne avec model+couleur+taille
            let found_index = cur_lines.findIndex((scan_line) => {
               return ((scan_line.sku === a_line.sku) && !scan_line.comment)  // On cherche une ligne existante par son SKU mais on exclut une ligne qui aurait un commentaire. Dans ce cas on ajoutera une autre ligne.
            })
            if (found_index < 0) {
               // Ligne n'existe pas, on la crée
               cur_lines = [...cur_lines, { ...a_line }]
            } else {
               // Ligne existe, on la met à jour ou on supprime si qty <= 0
               cur_lines[found_index] = { ...cur_lines[found_index], ...a_line }
            }
         }
      } else {
         // Sinon on peut updater la ligne ciblée
         cur_lines[update_line_num - 1] = { ...cur_lines[update_line_num - 1], ...new_lines[0] }
      }

      // On vire les qty <= 0
      cur_lines = cur_lines.filter(a_line => (a_line.qty > 0))

      // Blindage discount (retail) — source unique (cf. applyRetailDiscounts).
      cur_lines = applyRetailDiscounts(an_order, cur_lines)

      await db.orders.put({ ...an_order, lines: [...cur_lines] });
      awPushOrder(order_id)
   }, [getOrderById, awPushOrder, applyRetailDiscounts])

   const isRetailOrder = useCallback((order: Order | null | undefined): boolean => {
      return (order?.type === type_order.retail_interne || order?.type === type_order.retail_externe)
   }, [])

   /**
    * Nombre de commandes RETAIL cochées dans une liste (pour le singulier/pluriel des confirmations
    * de soumission en masse). Brique partagée Orders / CustomerDetail.
    */
   const countCheckedRetail = useCallback((orders: Order[] | null | undefined, checkedIds: Set<string>): number => {
      return (orders ?? []).filter(o => checkedIds.has(o.id as string) && isRetailOrder(o)).length
   }, [isRetailOrder])

   /**
    * Prédicat de recherche d'une commande, partagé par TOUS les onglets de la liste (En cours,
    * Soumises, À valider, Quotas, Traitée). Deux modes :
    *   - bySku=false : recherche dans la commande (id, customer_id, contremarque, nom/shortname client)
    *   - bySku=true  : recherche d'un SKU dans les lignes
    * Renvoie true si searchText est vide. (Commande sans customer connu → exclue, comme l'historique.)
    */
   const orderMatchesSearch = useCallback((order: Order | null | undefined, searchText: string | null | undefined, bySku: boolean): boolean => {
      const str = searchText?.trim().toLowerCase()
      if (!str) return true
      if (!order) return false
      // Recherche par SKU : INDÉPENDANTE du client (une commande dont le client n'est pas en base
      // locale reste trouvable par son SKU). Comparaison insensible à la casse.
      // - bijoux : sku composé "modèle-couleur-taille" → includes("modèle") matche.
      // - retail : sku = modèle "E-0444" (NON découpé) → includes("e-0444") matche.
      if (bySku) return order.lines?.some(l => l.sku?.toLowerCase().includes(str)) ?? false
      // Recherche "dans la commande" : inchangée (client requis pour nom/shortname).
      const c = getCustomerById(order.customer_id)
      if (!c) return false
      return String(order.id).toLowerCase().includes(str)
         || String(order.customer_id).toLowerCase().includes(str)
         || !!order.mark?.toLowerCase().includes(str)
         || !!c.name?.toLowerCase().includes(str)
         || !!c.shortname?.toLowerCase().includes(str)
   }, [getCustomerById])

   /**
    * Rôle effectif du user courant pour CET order. Refactoré chunk F (option B) :
    * la dérivation se base désormais sur `status + perms Appwrite + isRetailManager`,
    * plus sur `retail_n1_user_id`/`retail_n2_user_id` (retirés du schéma).
    *
    * Logique (par ordre de priorité) :
    *  - retailRole === 'admin' → 'admin' (admin global, intercepte tout)
    *  - retailRole === 'client' → 'client' (user-client, intercepte avant les checks
    *    manager pour éviter qu'un client soit identifié à tort comme valideur en
    *    cas d'ajout manuel de CRUD via console AW)
    *  - !isRetailManager → null (garde-fou : pas manager = pas valideur)
    *  - order.status === 'wait_n2' → 'N2' (si je vois le doc en wait_n2, je suis
    *    forcément N2 car le batch a posé les perms à ce moment-là)
    *  - order.status === 'wait_n1' → 'N1' (idem)
    *  - fallback matrice pour les statuts pré-validation (new, new_mgr, etc.) :
    *    on regarde supervisedN1/N2 pour savoir si le user supervise CE customer
    *
    * Le param `_currentUserId` reste dans la signature pour compat call sites
    * mais n'est plus utilisé (avant on comparait à retail_n*_user_id).
    */
   const getEffectiveRetailRole = useCallback((
      order: Order | null | undefined,
      _currentUserId: string | null | undefined,
      retailRole: string | null | undefined,
      roleForCustomer?: { isAdmin: boolean, isN1: boolean, isN2: boolean } | null,
      isRetailManager?: boolean,
   ): 'admin' | 'N1' | 'N2' | 'client' | null => {
      if (retailRole === 'admin') return 'admin'
      if (retailRole === 'client') return 'client'
      if (!isRetailManager) return null
      if (order?.status === 'wait_n2') return 'N2'
      if (order?.status === 'wait_n1') return 'N1'
      // Statuts pré-validation (new, new_mgr) : on regarde la
      // matrice de supervision pour savoir si le user supervise CE customer.
      if (roleForCustomer?.isN1) return 'N1'
      if (roleForCustomer?.isN2) return 'N2'
      return null
   }, [])

   /**
    * Indique si la chaîne de validation a un dernier maillon N1 avant M3. Sert d'INDICATION
    * UI (label/icône). La décision réelle de la transition (validate-n2 → wait_n1 ou locked)
    * est prise serveur-side par le webhook.
    *
    * Source d'autorité : `customer.has_n1_supervisor` (booléen maintenu par admin_api
    * depuis `user-client.prefs.supervisor_n1.length > 0`).
    *
    * NOTE : le paramètre `_order` (Order) reste dans la signature pour compat les
    * call sites existants, mais n'est plus utilisé (l'ancien fallback sur
    * `order.retail_n1_user_id` a été retiré dans le chunk F).
    */
   const hasN1Validator = useCallback((
      _order: Order | null | undefined,
      customer: Customer | null | undefined,
   ): boolean => {
      return customer?.has_n1_supervisor === true
   }, [])

   const isKOTOrder = useCallback((order: Order | null | undefined): boolean => {
      return order?.type === type_order.retail_quota
   }, [])

   const isKTMOrder = useCallback((order: Order | null | undefined): boolean => {
      return order?.type === type_order.ktm_quota
   }, [])

   /**
    * Retourne le type Retail d'un customer d'après son champ groupe_client (source M3 : CLI_RETGC).
    * La table de correspondance code → type est définie dans glob_const.customer_groups.
    */
   const getCustomerRetailType = useCallback((customer: Customer | null | undefined): 'RET' | 'ERE' | 'INT' | null => {
      const gc = customer?.groupe_client
      if (!gc) return null
      const grp = (glob_const.customer_groups as Record<string, { type: 'RET' | 'ERE' | 'INT' }>)[gc]
      return grp?.type ?? null
   }, [])

   /**
    * Retourne le type de commande Retail (ECR ou ECF) à créer pour ce customer
    * d'après la grille métier (DocRetail/GRILLE_ECR_ECF.txt → libs/retail_rules.ts),
    * ou null si aucune commande retail n'est possible pour ce client.
    */
   const getRetailOrderType = useCallback((customer: Customer | null | undefined): 'ECR' | 'ECF' | null => {
      return getRetailOrderTypeFromRules(customer)
   }, [])

   /**
    * Droit de créer une commande Retail pour ce client, basé sur la grille
    * officielle (DocRetail/GRILLE_ECR_ECF.txt → libs/retail_rules.ts).
    *
    * Un client dont l'une des dimensions (depot_retail, groupe_client,
    * zone_geo) manque est considéré comme NON éligible (renvoie false).
    */
   const canCreateRetailOrder = useCallback((customer: Customer | null | undefined): boolean => {
      return canCreateRetailOrderFromRules(customer)
   }, [])

   /**
    * Éligibilité à la création d'une COMMANDE BIJOUX (classique/BJX, hors Retail) pour le user courant.
    * Aligné sur la visibilité du catalogue BJX (cf. seeBJX) — sans accès au catalogue, pas de création :
    *   - admin                : oui (accès global)
    *   - user-client          : non (accès Retail/ECR-ECF uniquement)
    *   - manager Retail (N1/N2): non (ne voit pas le catalogue BJX)
    *   - autres (interne…)    : oui
    */
   const canCreateBijouxOrder = useCallback((): boolean => {
      // Source unique : seuls admin et internal créent du bijoux (cf. useCanSeeJewelry).
      // N1/N2/client et autres rôles retail = retail-only.
      const s = usePrefStore.getState()
      return computeCanSeeJewelry(s.retailRole, s.serverRetailRole, s.isRetailManager, s.prefsLoaded)
   }, [])

   /**
    * Remise applicable à une famille retail pour un client donné, d'après la grille :
    *   - 0 ou 100 si la famille est autorisée pour ce client
    *   - null si la famille est interdite (catalogue masqué) ou si le client n'a pas accès au retail
    *   - pour un client null (admin/N1/N2 sans client courant) : 0 si famille ∈ RETAIL_FAMS, sinon null
    */
   const getRetailFamilleDiscount = useCallback((retail_famille_code: string | null | undefined, customer: Customer | null | undefined) => {
      return getRetailFamilleDiscountFromRules(retail_famille_code, customer)
   }, [])

   /**
    * Clonage d'une commande
    *
    * @param newMark — si fourni, override la contremarque générée. Utilisé pour
    *                  les KOT/KTM dont la contremarque doit être unique
    *                  (le caller doit avoir validé l'unicité au préalable).
    */
   const cloneOrder = useCallback(async (order_id: string, to_cust_id: string | null = null, newMark: string | null = null): Promise<string | null> => {
      const orig = await getOrderById(order_id)
      const now = moment()

      if (orig) {
         // Verrou primitive : dupliquer un KOT = créer un nouveau quota → réservé admin/N1 du
         // customer concerné (défense en profondeur, en plus des gardes UI canWriteKOT).
         if (isKOTOrder(orig)) {
            const s = usePrefStore.getState()
            const r = computeRetailRoleForCustomer(orig.customer_id, s.retailRole, s.isRetailManager, s.supervisedN1, s.supervisedN2)
            if (!(r.isAdmin || r.isN1)) {
               console.warn('cloneOrder refusé : un KOT ne peut être dupliqué que par admin/N1 du customer')
               return null
            }
         }
         let region: string | null | undefined
         let currency: string | null | undefined
         if (to_cust_id !== null) {
            const customer = getCustomerById(to_cust_id)

            if (customer) {
               orig.customer_id = to_cust_id
               currency = customer.tarifs.ces.currency  // Dans le menu on n'est pas forcément sur le client ou la commande sélectionné(e), on doit donc cibler explicitement la bonne devise
               region = findRegionFromCessionTarifNames(customer.tarifs.ces.tarif, customer.tarifs.ces.currency)
               if (!region) {
                  // !!!!! NB : OUI, c'est bien CES qui est pris dans a_customer pour chercher la région. C'est pour les clients qui n'ont pas de tarif cession mais un tarif public remisé
                  region = findRegionFromPublicTarifNames(customer?.tarifs.ces.tarif, customer?.tarifs.ces.currency)
               }
            }
         }
         // orig.type = type_order.commande
         orig.date = now.format("YYYY-MM-DD")
         orig.time = now.format("HH:mm")
         orig.delivery = now.add(default_delivery_delay, 'days').format("YYYY-MM-DD")
         orig.comment = null
         if (newMark !== null) {
            orig.mark = newMark
         } else {
            orig.mark = isKOTOrder(orig) ? `QUOTAS_${now.format('YYYYMM')}` : isKTMOrder(orig) ? orig.mark : null
         }
         orig.signature = null
         orig.readonly = false
         orig.last_pushed = null
         orig.last_updated = null
         // On reconstruit les lignes pour exclure des champs inutiles (remain_qty par exemple), qui peuvent exister dans les commandes BI si on clone une commande BI !
         // Le discount retail est (re)posé par createOrder via applyRetailDiscounts.
         orig.lines = orig.lines.map(a_line => {
            let price = a_line.price
            if (orig && region && currency) {
               const obj_price = getCessPriceForModelBySKU(a_line.sku, region, currency)
               if (obj_price) {
                  price = obj_price.price
               }
            }
            return {
               sku: a_line.sku,
               qty: a_line.qty,
               price,
               ...(a_line.comment ? { comment: a_line.comment } : {}),
            }
         })
         return await createOrder(orig)
      } else {
         return null
      }
   }, [createOrder, findRegionFromCessionTarifNames, findRegionFromPublicTarifNames, getCessPriceForModelBySKU, getCustomerById, getOrderById, isKOTOrder, isKTMOrder])

   /**
    * Suppression d'une ligne dans une commande à partir de son numéro de ligne
    */
   const deleteSingleLineInOrder = useCallback(async (order_id: string, line_num: number) => {
      const an_order = await getOrderById(order_id)

      let cur_lines = an_order.lines

      cur_lines = cur_lines.filter((a_line, num) => {
         return (num + 1) !== line_num
      })

      cur_lines = applyRetailDiscounts(an_order, cur_lines)   // blindage discount (heal des lignes restantes)
      await db.orders.put({ ...an_order, lines: [...cur_lines] });
      awPushOrder(order_id)
   }, [getOrderById, awPushOrder, applyRetailDiscounts])

   /**
    * Suppression de lignes dans une commande à partir de SKUs
    * Reçoit un tableau de SKUs, même si l'usage sera sans doute des SKU individuels
    */
   const deleteLinesInOrder = useCallback(async (order_id: string, skus: string[]) => {
      const an_order = await getOrderById(order_id)

      let cur_lines = an_order.lines

      cur_lines = cur_lines.filter(a_line => {
         return !skus.includes(a_line.sku)
      })

      cur_lines = applyRetailDiscounts(an_order, cur_lines)   // blindage discount (heal des lignes restantes)
      await db.orders.put({ ...an_order, lines: [...cur_lines] });
      awPushOrder(order_id)
   }, [getOrderById, awPushOrder, applyRetailDiscounts])

   /**
    * MàJ d'attributs d'une commande
    * Seuls les attributs spécifiés dans l'objet
    */
   const _updateOrder = useCallback(async (order_id: string, attribs: Partial<Order>, push: boolean) => {
      const an_order = await getOrderById(order_id)

      if (!an_order.readonly || (an_order.status === order_status.locked) || (an_order.status === order_status.locked4sync) || (an_order.status === order_status.wait_n2) || (an_order.status === order_status.wait_n1)) {
         console.log('AN_ORDER', an_order);
         console.log('ATTRIBS', attribs);
         const newAttribs = { ...an_order, ...attribs } as Order
         // Si la maj touche les lignes (ex. validation N1/N2 via RetailValidationTable), on blinde le discount retail.
         if (attribs.lines) newAttribs.lines = applyRetailDiscounts(newAttribs, newAttribs.lines)
         await db.orders.put(newAttribs);
         if (push) {
            awPushOrder(order_id)
         }
         // On ne désélectionne QUE si la commande a quitté un état "en édition".
         // new_mgr (brouillon manager) est un brouillon comme new : il faut garder la
         // sélection, sinon chaque ajout de ligne (qui repushe → updateOrderLocally
         // last_pushed) remet curOrder à null.
         if (newAttribs.status !== order_status.new && newAttribs.status !== order_status.new_mgr && newAttribs.status !== order_status.wait_n2 && newAttribs.status !== order_status.wait_n1) {
            setCurOrder(null)
         }
      } else {
         console.log(`updateOrder(${order_id}) - Read only!`);
      }
   }, [getOrderById, awPushOrder, setCurOrder, applyRetailDiscounts])

   const updateOrderLocally = useCallback(async (order_id: string, attribs: Partial<Order>) => {
      return await _updateOrder(order_id, attribs, false)
   }, [_updateOrder])

   const updateOrder = useCallback(async (order_id: string, attribs: Partial<Order>) => {
      return await _updateOrder(order_id, attribs, true)
   }, [_updateOrder])

   /**
    * Suppression d'une commande
    */
   const deleteOrder = useCallback(async (order_id: string) => {
      // Verrou primitive : un KOT ne peut être supprimé que par admin/N1 du customer concerné
      // (défense en profondeur, en plus des gardes UI canWriteKOT).
      const to_delete = await getOrderById(order_id)
      if (isKOTOrder(to_delete)) {
         const s = usePrefStore.getState()
         const r = computeRetailRoleForCustomer(to_delete?.customer_id, s.retailRole, s.isRetailManager, s.supervisedN1, s.supervisedN2)
         if (!(r.isAdmin || r.isN1)) {
            console.warn('deleteOrder refusé : un KOT ne peut être supprimé que par admin/N1 du customer')
            return
         }
      }
      if (curOrder === order_id) {
         setCurOrder(null)
      }
      await updateOrderLocally(order_id, {
         status: order_status.deleted,   // On la passe en deleted localement, ce n'est pas un état stocké sur le serveur
         readonly: true,
      })
      await awDeleteOrder(order_id)
      removeOrderIdFromRecentOrders(order_id)
   }, [awDeleteOrder, curOrder, setCurOrder, updateOrderLocally, removeOrderIdFromRecentOrders, getOrderById, isKOTOrder])


   /**
    * Compare les lignes originales avec les lignes actuelles et retourne les quantités refusées.
    * Règle : seules les baisses partielles produisent un reliquat (0 < qté finale < qté demandée,
    * qty = demandée − finale). Une ligne mise à 0 ou supprimée est un refus net : elle n'est pas
    * renvoyée et ne part donc pas en reliquat.
    */
   const computeRefusedLines = useCallback((originalLines: OrderLine[], currentLines: OrderLine[]): OrderLine[] => {
      const currentMap = new Map<string, OrderLine>()
      for (const line of currentLines) {
         currentMap.set(line.sku, line)
      }

      const refused: OrderLine[] = []
      for (const origLine of originalLines) {
         const curLine = currentMap.get(origLine.sku)
         const currentQty = Number(curLine?.qty)
         if (!(currentQty > 0)) continue   // absente, 0, nulle ou non numérique : refus net
         const refusedQty = origLine.qty - currentQty

         if (refusedQty > 0) {
            refused.push({
               sku: origLine.sku,
               qty: refusedQty,
               ...(origLine.price != null ? { price: origLine.price } : {}),
            })
         }
      }
      return refused
   }, [])

   // NB : la création du reliquat est désormais 100% SERVEUR (webhook validate-n1 / validate-n2 →
   // upsertReliquat). La PWA se contente de transmettre les lignes refusées cochées à
   // validateRetailN1/N2. Plus de createOrUpdateReliquat/findReliquatOrder côté client (pas de copie
   // locale orpheline chez le valideur, perms client+admins posées par le serveur).

   /**
    * Transitions du workflow retail via les webhooks (la fonction serveur unique décide le
    * statut cible + pose les perms). Pattern commun : on POUSSE d'abord l'état local vers AW
    * (lignes à jour), on appelle le webhook (transition + perms serveur-side), puis on
    * réconcilie le statut local SANS push (l'appelant peut avoir perdu ses perms après la
    * transition → ne plus repousser via updateOrder). Retournent le nouveau statut ou null.
    * NB : c'est le serveur qui décide wait_n2 vs wait_n1 vs locked (plus de hasN1 côté PWA).
    */
   const submitRetailOrder = useCallback(async (order: Order): Promise<string | null> => {
      if (!order?.id) return null
      // Double-check sécurité : on ne soumet qu'un brouillon client 'new' (le serveur revérifie).
      if (order.status !== order_status.new) {
         console.warn(`submitRetailOrder refusé : statut '${order.status}' (attendu 'new')`)
         return null
      }
      try {
         // Snapshot des lignes soumises + push (statut encore 'new', le client a ses perms).
         await updateOrderLocally(order.id, { original_lines: order.lines })
         await awPushOrder(order.id)
         const newStatus = await awSubmitOrder(order.id)
         if (newStatus) {
            await updateOrderLocally(order.id, { status: newStatus, readonly: true })
            // Soumise → elle quitte le périmètre éditable du client : si c'était la commande
            // courante, on l'« oublie » (curOrder → null).
            if (curOrder === order.id) setCurOrder(null)
         }
         return newStatus
      } catch (err) {
         // Pas de clean local : c'est la commande du client (brouillon valide), il pourra réessayer.
         await onRetailWebhookError(err, order.id, false)
         return null
      }
   }, [updateOrderLocally, awPushOrder, awSubmitOrder, onRetailWebhookError, curOrder, setCurOrder])

   // reliquatLines (optionnel) = lignes refusées que le valideur veut reporter en reliquat ; le
   // SERVEUR ne crée le reliquat que si la validation produit 'locked' (cf. webhook upsertReliquat).
   const validateRetailN2 = useCallback(async (order: Order, reliquatLines?: { sku: string, qty: number }[]): Promise<string | null> => {
      if (!order?.id) return null
      // Double-check sécurité : N2 ne valide qu'une commande 'new' ou 'wait_n2' (le serveur revérifie rôle + statut).
      if (order.status !== order_status.new && order.status !== order_status.new_mgr && order.status !== order_status.wait_n2) {
         console.warn(`validateRetailN2 refusé : statut '${order.status}' (attendu 'new', 'new_mgr' ou 'wait_n2')`)
         return null
      }
      try {
         const pushOk = await awPushOrder(order.id)   // pousse les lignes courantes (édits N2) avant transition
         if (!pushOk) {
            // Enregistrement AW refusé (droits/réseau) → UN seul toast ici, on NE rappelle PAS le webhook
            // (éviterait un 2e toast). On resynchronise la copie locale avec la vérité serveur.
            toast.error(trans('?order_save_failed'), { position: 'top-right', autoClose: 4000, theme: 'colored', toastId: 'order-save-failed' })
            try { await awReconcileOrder(order.id, { afterFailedPush: true }) } catch { /* infra : on laisse la copie locale */ }
            return null
         }
         const newStatus = await awValidateN2(order.id, reliquatLines)   // → wait_n1 ou locked (serveur) + reliquat si locked
         if (newStatus) await updateOrderLocally(order.id, { status: newStatus, readonly: newStatus !== order_status.wait_n1 })
         return newStatus
      } catch (err) {
         await onRetailWebhookError(err, order.id, true) // stale → retire la copie locale (plus à moi de valider)
         return null
      }
   }, [awPushOrder, awValidateN2, updateOrderLocally, onRetailWebhookError, trans, awReconcileOrder])

   const validateRetailN1 = useCallback(async (order: Order, reliquatLines?: { sku: string, qty: number }[]): Promise<string | null> => {
      if (!order?.id) return null
      // Double-check sécurité : N1/admin ne valide que 'new' / 'wait_n2' / 'wait_n1' (le serveur revérifie rôle + statut).
      if (order.status !== order_status.new && order.status !== order_status.new_mgr && order.status !== order_status.wait_n2 && order.status !== order_status.wait_n1) {
         console.warn(`validateRetailN1 refusé : statut '${order.status}' (attendu 'new', 'new_mgr', 'wait_n2' ou 'wait_n1')`)
         return null
      }
      try {
         const pushOk = await awPushOrder(order.id)
         if (!pushOk) {
            // Enregistrement AW refusé (droits/réseau) → UN seul toast ici, on NE rappelle PAS le webhook
            // (éviterait un 2e toast). On resynchronise la copie locale avec la vérité serveur.
            toast.error(trans('?order_save_failed'), { position: 'top-right', autoClose: 4000, theme: 'colored', toastId: 'order-save-failed' })
            try { await awReconcileOrder(order.id, { afterFailedPush: true }) } catch { /* infra : on laisse la copie locale */ }
            return null
         }
         const newStatus = await awValidateN1(order.id, reliquatLines)   // → locked + reliquat (serveur)
         if (newStatus) await updateOrderLocally(order.id, { status: newStatus, readonly: true })
         return newStatus
      } catch (err) {
         await onRetailWebhookError(err, order.id, true)
         return null
      }
   }, [awPushOrder, awValidateN1, updateOrderLocally, onRetailWebhookError, trans, awReconcileOrder])

   // Renvoi au user-client (N1/N2/admin) : statut courant → new (perms client+admins serveur-side).
   // On POUSSE d'abord les lignes locales (le brouillon de validation auto-committé en local) vers AW,
   // sinon les éditions du valideur seraient perdues côté AW au retour client.
   const returnRetailToClient = useCallback(async (order: Order): Promise<string | null> => {
      if (!order?.id) return null
      // Double-check sécurité : on ne renvoie au client qu'une commande en cours de validation
      // ('wait_n2' ou 'wait_n1') ; le serveur revérifie rôle + statut.
      if (order.status !== order_status.wait_n2 && order.status !== order_status.wait_n1) {
         console.warn(`returnRetailToClient refusé : statut '${order.status}' (attendu 'wait_n2' ou 'wait_n1')`)
         return null
      }
      try {
         await awPushOrder(order.id)   // sauvegarde des éditions courantes avant la transition
         const newStatus = await awReturnToClient(order.id)
         if (newStatus) await updateOrderLocally(order.id, { status: newStatus, readonly: false, original_lines: undefined, retail_status_chg: null, retail_warned: null })
         return newStatus
      } catch (err) {
         await onRetailWebhookError(err, order.id, true)
         return null
      }
   }, [awPushOrder, awReturnToClient, updateOrderLocally, onRetailWebhookError])

   /**
    * Envoi d'un brouillon manager (new_mgr) au user-client → webhook send-to-client (statut 'new',
    * perms client+admins). Le manager (sauf admin) PERD ses droits côté AW : comme Realtime n'émet
    * aucun event de retrait de perms, on réconcilie la copie locale (awReconcileOrder) → elle est
    * purgée si on n'y a plus accès (N1/N2 non-admin), ou mise à jour en 'new' si on les conserve
    * (admin). Sinon la commande resterait visible à tort dans la base locale du manager.
    */
   const sendRetailToClient = useCallback(async (order: Order): Promise<boolean> => {
      if (order?.status !== order_status.new_mgr) {
         console.warn(`sendRetailToClient refusé : statut '${order?.status}' (attendu 'new_mgr')`)
         return false
      }
      try {
         await awPushOrder(order.id)   // lignes courantes (édits manager) avant la transition
         const ok = await awSendOrderToClient(order.id)
         if (ok) {
            try { await awReconcileOrder(order.id) } catch { /* infra : on laisse la copie locale telle quelle */ }
         }
         return ok
      } catch (err) {
         await onRetailWebhookError(err, order.id, true)
         return false
      }
   }, [awPushOrder, awSendOrderToClient, awReconcileOrder, onRetailWebhookError])

   /**
    * Retourne si on peut synchroniser une commande (true) ou pas (false)
    * Doit :
    * - ne pas être une commande déjà sync
    * - avoir une adresse de livraison (écrin pour retail, bijoux sinon, sauf KOT/KTM)
    * - avoir une contremarque
    */
   /**
    * Complétude "métier" d'une commande : contremarque (mark) renseignée ET adresse de
    * livraison présente (écrin pour retail interne/externe, bijoux sinon). Indépendant du
    * statut / readonly. Brique partagée par canSyncOrder (soumission client) et par le
    * gating de soumission/validation d'un brouillon manager (new_mgr) : on n'autorise la
    * soumission à validation / validation finale que si la commande est complète.
    */
   const isOrderComplete = useCallback((order: Order | null | undefined): boolean => {
      if (!order || !order.mark) return false
      if (order.type === type_order.retail_interne || order.type === type_order.retail_externe) {
         return !!order.addresses?.ecrins
      }
      return !!order.addresses?.jewels
   }, [])

   const canSyncOrder = useCallback((order: Order | null | undefined): boolean => {
      if (!order || order.readonly) return false
      // KOT/KTM : envoyées sur AppWrite mais jamais synchronisées vers M3
      if (order.type === type_order.retail_quota || order.type === type_order.ktm_quota) return false
      return isOrderComplete(order)
   }, [isOrderComplete])

   /**
    * Un KOT (quota par client) n'est ÉCRIVABLE que par un admin ou un N1 du customer concerné :
    * sélectionner, modifier, dupliquer (= créer un nouveau quota) et supprimer.
    * Tout autre rôle (user-client, N2, interne) : consultation seule des lignes.
    *
    * Le N2 et le user-client ont bien le document KOT en local (Dexie) — les perms AppWrite leur
    * donnent le `read` (cf. back/libs/perms.js : R = admin + N1 + N2 + user-clients, U+D = admin + N1)
    * — mais uniquement pour alimenter le quota du QtyPickerRetail. Ils n'y ont pas accès en tant que
    * document. Cette garde est le pendant UI de ce contrat de perms : la garder alignée dessus.
    *
    * Pour une commande non-KOT, la règle ne s'applique pas (true). `roleForCustomer` = résultat de
    * useRetailRoleForCustomer/computeRetailRoleForCustomer pour le customer de la commande.
    */
   const canWriteKOT = useCallback((order: Order | null | undefined, roleForCustomer?: { isAdmin?: boolean, isN1?: boolean } | null): boolean => {
      if (!isKOTOrder(order)) return true
      return !!(roleForCustomer?.isAdmin || roleForCustomer?.isN1)
   }, [isKOTOrder])

   return {
      order_status,
      type_address,
      tag_address,
      type_order,
      // fetchOrders,
      getCustomers,
      getCustomersAsArray,
      getCustomersAsSortedArray,
      getCustomerById,
      setCustomerIdAsCurrent,
      selectCustomerWithTarif,
      autoSelectSingleCustomer,
      getSiegeForCustomerId,
      getSiegeFromCustomer,
      getContactsFromCustomer,
      getContactsFromCustomerAsArray,
      getContactsFromCustomerAsSortedArray,
      getAddressesFromCustomer,
      getAddressesFromCustomerAsArray,
      getAddressesFromCustomerAsSortedArray,
      sortCustomerAddresses,
      getPhoneForCustomerId,
      updateRecentCustomers,
      removeOrderIdFromRecentOrders,
      getSyncOrders,
      getUnsyncOrders,
      getSyncOrdersAsArray,
      getUnsyncOrdersAsArray,
      getOrderById,
      getOrdersByIds,
      setOrderIdAsCurrent,
      isOrderSubmittedOrLocked,
      getSyncOrdersForCustomerId,
      getUnsyncOrdersForCustomerId,
      getOrderIdsForCustomerId,
      updateRecentOrders,
      calcTotalForOrder,
      getItemsCountForOrder,
      makeTemplateOrder,
      createOrder,
      createOrUpdateLinesInOrder,
      cloneOrder,
      deleteSingleLineInOrder,
      deleteLinesInOrder,
      updateOrder,
      updateOrderLocally,
      deleteOrder,
      isRetailOrder,
      countCheckedRetail,
      orderMatchesSearch,
      getEffectiveRetailRole,
      hasN1Validator,
      isKOTOrder,
      isKTMOrder,
      getCustomerRetailType,
      getRetailOrderType,
      canCreateRetailOrder,
      canCreateBijouxOrder,
      getRetailFamilleDiscount,
      canSyncOrder,
      isOrderComplete,
      canWriteKOT,
      computeRefusedLines,
      submitRetailOrder,
      validateRetailN2,
      validateRetailN1,
      returnRetailToClient,
      sendRetailToClient
   }
}

export default useSales
